import type { ParsedUrlQuery } from "querystring";

import type { ProductUnknown } from "@/common/contracts/product.contracts";
import { CURRENCY_URL_PARAM } from "@/features/display-currency/currency-url";
import type { PageParams, SearchParams } from "@/features/search/api/queries";
import {
  SEARCH_ROUTE_PARAM,
  type SearchRoute,
} from "@/features/search/api/route";
import type { Product } from "@/features/search/api/types";
import type { ProductCardProduct } from "@/features/search/components/product-card/product-card.context";
import { toCardProduct } from "@/features/search/utils/gateway-product.utils";
import { isTrackingKey } from "@/features/search/utils/search-params.utils";
import {
  US_SHIPPABLE_SEARCH_PARAM,
  US_SHIPPABLE_SEARCH_VALUE,
} from "@/features/us-market/us-shippable";
import { type ContentLocale, toContentLocale } from "@/i18n/config";

/**
 * "New this week" — the twelve newest live listings on a landing page, linked from that page.
 *
 * Search Console lists ~824k product URLs as "crawled, currently not indexed"; a sample of them
 * were live listings with a median age of ten days. A new listing is discovered by crawl but has
 * next to no internal links on day one, so this module puts the freshest inventory of every
 * category, brand and style page into the server-rendered HTML of an indexable page immediately.
 * The rules for when it is fetched, and the shape it is sent to the browser in, live here so the
 * loader and the tests read one definition.
 */

/** How many cards the module shows. */
export const NEW_THIS_WEEK_LIMIT = 12;

/** Below this many results in the main grid the module would only repeat the grid. */
export const NEW_THIS_WEEK_MIN_TOTAL = 24;

/** Fewer cards than this reads as an accident, not a module. */
export const NEW_THIS_WEEK_MIN_ITEMS = 4;

/**
 * Routing plumbing Next.js injects into `router.query` — mirrors the private check in
 * `search-params.utils.ts`; those keys are not search state.
 */
const isRouterInternalKey = (key: string) =>
  key === "nextInternalLocale" || key.startsWith("nxtP");

/**
 * Whether the page qualifies for the module.
 *
 * Only a landing page (category, brand, style, curated) — a free-text `/search` has no category
 * to be new in. And only its plain form: a URL carrying a filter or a sort is a narrowing the
 * visitor asked for, and the module would show items outside it. `page=N` is the one param that
 * stays, since page 2 of a category is still that category, and it is where the fresh links are
 * least likely to be duplicated by the grid.
 */
export const shouldFetchNewThisWeek = (
  route: SearchRoute,
  query: ParsedUrlQuery
): boolean => {
  if (route.kind !== "page") {
    return false;
  }

  return Object.entries(query).every(
    ([key, value]) =>
      key === SEARCH_ROUTE_PARAM ||
      key === "page" ||
      /* WHO-6199: the display currency changes what the prices are in, not which items show. */
      key === CURRENCY_URL_PARAM ||
      value == null ||
      value === "" ||
      isRouterInternalKey(key) ||
      isTrackingKey(key)
  );
};

/**
 * The second call's params: the same landing page and language, newest first, one page of
 * twelve. The route params carry no user filters when `shouldFetchNewThisWeek` said yes, so
 * only the view state is overridden — the page's own rules (its availability filter included)
 * stay whatever the service applies to the main grid.
 */
export const newThisWeekParams = (params: PageParams): PageParams => ({
  ...params,
  page: 1,
  limit: NEW_THIS_WEEK_LIMIT,
  sort: "created",
  order: "desc",
});

/** Whether a result set is large enough for the module to add anything. */
export const hasEnoughResultsForNewThisWeek = (
  total: number | null | undefined
): boolean => (total ?? 0) >= NEW_THIS_WEEK_MIN_TOTAL;

/**
 * A search `Product` cut down to what the card reads. The full DTO carries a description,
 * dimensions and ids the card never shows; sending twelve of those in the page props would be
 * most of the module's weight for nothing.
 *
 * `favoriteId` goes out null: the module is fetched anonymously so the HTML is the same for
 * every visitor, and the heart is a signed-in feature the main grid already serves.
 */
export const toNewThisWeekProduct = (product: Product): ProductCardProduct => ({
  id: product.id,
  slug: product.slug,
  title: product.title,
  currency: product.currency,
  originalPrice: product.originalPrice,
  buyNowPrice: product.buyNowPrice,
  discountPercentage: product.discountPercentage,
  bidFrom: product.bidFrom,
  sold: product.sold,
  soldAtPrice: product.soldAtPrice,
  numberOfItems: product.numberOfItems,
  /* The card shows the first two photos; the rest would ride along unused. */
  images: product.images.slice(0, 2).map(image => ({
    url: image.url,
    width: image.width,
    height: image.height,
  })),
  favoriteId: null,
  labels: product.labels,
});

export type NewThisWeekLocale = ContentLocale;

type NewThisWeekCopy = { title: string; subtitle: string };

/**
 * Default strings per locale, used as `defaultValue` until the Lokalise keys exist
 * (`listing:listing-new-this-week-title` / `-subtitle`). Translations are not seeded from this
 * repo, so without these a fresh deploy would print the raw keys.
 */
export const NEW_THIS_WEEK_COPY: Record<NewThisWeekLocale, NewThisWeekCopy> = {
  en: {
    title: "New this week",
    subtitle: "Fresh finds just listed in this category",
  },
  nl: {
    title: "Nieuw deze week",
    subtitle: "Net geplaatst in deze categorie",
  },
  de: {
    title: "Neu diese Woche",
    subtitle: "Gerade erst in dieser Kategorie eingestellt",
  },
  fr: {
    title: "Nouveautés de la semaine",
    subtitle: "Tout juste mis en ligne dans cette catégorie",
  },
  es: {
    title: "Novedades de la semana",
    subtitle: "Recién publicados en esta categoría",
  },
  it: {
    title: "Novità della settimana",
    subtitle: "Appena pubblicati in questa categoria",
  },
};

/** The default copy for a Next locale (`en-us` reads `en`); English for anything unknown. */
export const newThisWeekCopy = (locale?: string): NewThisWeekCopy =>
  NEW_THIS_WEEK_COPY[toContentLocale(locale)] ?? NEW_THIS_WEEK_COPY.en;

/**
 * The homepage asks for a few more than it shows: the locale-wide `/search` has no landing-page
 * rule filtering out sold items, so those are dropped here and the rest still fills twelve cards and the spares below.
 */
export const NEW_THIS_WEEK_HOMEPAGE_FETCH_LIMIT = 30;

/**
 * The homepage's call: every category in the page's language, newest first. No query, so the
 * result is the same for every visitor and can be baked into the ISR page. The one facet is the
 * US market's (WHO-6510): `/en-us` is built with only what ships to the US, like its lists.
 */
export const newThisWeekHomepageParams = (
  lang: NonNullable<SearchParams["lang"]>,
  usShippableOnly = false
): SearchParams => ({
  query: "",
  lang,
  page: 1,
  limit: NEW_THIS_WEEK_HOMEPAGE_FETCH_LIMIT,
  sort: "created",
  order: "desc",
  ...(usShippableOnly
    ? { [US_SHIPPABLE_SEARCH_PARAM]: [US_SHIPPABLE_SEARCH_VALUE] }
    : {}),
});

/**
 * Spare cards the homepage carries beyond the twelve it shows. After mount the section drops any
 * product the For-you block above it already shows, and the spares fill those slots so the
 * section keeps twelve cards.
 */
export const NEW_THIS_WEEK_HOMEPAGE_SPARES = 6;

/** Twelve shown plus the spares. */
export const NEW_THIS_WEEK_HOMEPAGE_KEEP =
  NEW_THIS_WEEK_LIMIT + NEW_THIS_WEEK_HOMEPAGE_SPARES;

/** The newest live products of a homepage result, pruned for the card, twelve plus the spares. */
export const toNewThisWeekHomepageProducts = (
  products: Product[]
): ProductCardProduct[] =>
  products
    .filter(product => !product.sold)
    .slice(0, NEW_THIS_WEEK_HOMEPAGE_KEEP)
    .map(toNewThisWeekProduct);

/**
 * The twelve cards the homepage shows: the list minus anything in `excludeIds` (the For-you
 * block's products), topped up from the spares. With nothing to exclude it is the first twelve,
 * which is exactly what the server renders.
 */
export const dedupeNewThisWeekHomepage = (
  products: ProductCardProduct[],
  excludeIds: ReadonlySet<string>
): ProductCardProduct[] =>
  products
    .filter(product => !excludeIds.has(product.id))
    .slice(0, NEW_THIS_WEEK_LIMIT);

type NewThisWeekHomepageCopy = NewThisWeekCopy & { viewAll: string };

/**
 * The homepage's default strings: the listing subline says "in this category", which the
 * homepage is not, and the homepage adds a link to the full "new" listing page.
 */
export const NEW_THIS_WEEK_HOMEPAGE_COPY: Record<
  NewThisWeekLocale,
  NewThisWeekHomepageCopy
> = {
  en: {
    title: "New this week",
    subtitle: "Fresh finds, just listed",
    viewAll: "View all new",
  },
  nl: {
    title: "Nieuw deze week",
    subtitle: "Vers binnen, net geplaatst",
    viewAll: "Bekijk alles wat nieuw is",
  },
  de: {
    title: "Neu diese Woche",
    subtitle: "Gerade erst eingestellt",
    viewAll: "Alle Neuheiten ansehen",
  },
  fr: {
    title: "Nouveautés de la semaine",
    subtitle: "Tout juste mis en ligne",
    viewAll: "Voir toutes les nouveautés",
  },
  es: {
    title: "Novedades de la semana",
    subtitle: "Recién publicados",
    viewAll: "Ver todas las novedades",
  },
  it: {
    title: "Novità della settimana",
    subtitle: "Appena pubblicati",
    viewAll: "Vedi tutte le novità",
  },
};

/** The homepage default copy for a Next locale (`en-us` reads `en`); English for anything unknown. */
export const newThisWeekHomepageCopy = (
  locale?: string
): NewThisWeekHomepageCopy =>
  NEW_THIS_WEEK_HOMEPAGE_COPY[toContentLocale(locale)] ??
  NEW_THIS_WEEK_HOMEPAGE_COPY.en;

/**
 * The gateway's curated new-in items as homepage cards: nulls, items without an id and sold items
 * dropped, twelve plus the spares kept, the gateway's order untouched.
 */
export const toCuratedNewInHomepageProducts = (
  items: ReadonlyArray<ProductUnknown | null | undefined>
): ProductCardProduct[] =>
  items
    .flatMap(item => (item?.id ? [toCardProduct(item)] : []))
    .filter(product => !product.sold)
    .slice(0, NEW_THIS_WEEK_HOMEPAGE_KEEP)
    .map(product => ({ ...product, images: product.images.slice(0, 2) }));
